import { forwardRef, useState } from 'react';

import { useDateTimeContext } from '@rtkwlf/fenrir-react';
import type { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

import {
  DateTimePicker as FenrirDateTimePicker,
  DateTimeInputField,
  Box,
} from '@packages/fenrir/components';
import type {
  BoxProps,
  DateTimeValidationError,
  DateTimePickerProps as FenrirDateTimePickerProps,
} from '@packages/fenrir/components';
import type { TextFieldProps } from '@packages/fenrir/components/text-field';

import { useDateTimePickerErrorMessages } from './use-date-time-picker-error-messages/use-date-time-picker-error-messages';

export type DateTimePickerProps = FenrirDateTimePickerProps<Dayjs> & {
  label: string;
  errorMessage?: string;
  helperText?: string;
  wrapperProps?: BoxProps;
  onError?: (error: DateTimeValidationError, value: Dayjs) => void;
  pickerProps?: Omit<
    FenrirDateTimePickerProps<Dayjs>,
    | 'disabled'
    | 'maxDate'
    | 'minDate'
    | 'name'
    | 'onError'
    | 'onChange'
    | 'slotProps'
    | 'timezone'
    | 'value'
  >;
  placeholder?: string;
  required?: boolean;
  textFieldProps?: Omit<
    TextFieldProps,
    'helperText' | 'id' | 'error' | 'placeholder' | 'required'
  >;
  clearable?: boolean;
};

export const DateTimePicker = forwardRef<HTMLDivElement, DateTimePickerProps>(
  (
    {
      label,
      minDateTime,
      maxDateTime,
      errorMessage,
      helperText,
      wrapperProps,
      onError,
      pickerProps,
      placeholder,
      required,
      textFieldProps,
      value,
      clearable = false,
      ...props
    }: DateTimePickerProps,
    ref,
  ) => {
    const { t } = useTranslation();
    const { timezone } = useDateTimeContext();
    const [dateTimePickerError, setDateTimePickerError] =
      useState<DateTimeValidationError | null>(null);

    const labelWithTimezone = label
      ? t('ui:timezone.label', {
          label,
          timezone: t(`ui:timezone.${timezone}`),
        })
      : undefined;

    const handleError = (
      newError: DateTimeValidationError,
      updatedValue: Dayjs | null,
    ) => {
      setDateTimePickerError(newError);
      onError?.(newError, updatedValue);
    };

    const dateTimePickerErrorMessages = useDateTimePickerErrorMessages({
      error: dateTimePickerError,
      minDate: minDateTime,
      maxDate: maxDateTime,
    });

    return (
      <Box ref={ref} {...wrapperProps}>
        <FenrirDateTimePicker
          {...props}
          onError={handleError}
          slots={{ textField: DateTimeInputField }}
          slotProps={{
            textField: {
              label: labelWithTimezone,
              error: !!errorMessage || !!dateTimePickerError,
              helperText:
                errorMessage || dateTimePickerErrorMessages || helperText,
              placeholder,
              required,
              ...textFieldProps,
            },
            field: { clearable },
          }}
          timezone={timezone === 'local' ? 'system' : 'UTC'}
          value={value}
          {...pickerProps}
        />
      </Box>
    );
  },
);
